/* +++ / MP / +++ /
   TEMPORARY DIAGNOSTIC — v8. Remove once the PDP scroll-jump is understood.

   Why this exists: tapping a colour swatch throws the page to the top on
   iPhone, on the LIVE theme, in both orientations. Quinn instrumented it in
   Chromium 2026-08-06 and it does not reproduce there at all — 366 rAF
   samples, scrollY constant, scrollHeight constant, zero programmatic scroll
   calls from a self-tested probe. The mover is WebKit-specific and we have no
   WebKit automation, so the only instrument left is Steve's phone. This
   renders on the page: no Mac, no cable, no Web Inspector.

   INSTRUMENT LIMIT (Quinn, and it is why this samples layout too): patching
   HTMLElement.prototype.focus cannot see a NATIVE focus from a real tap,
   because the browser never calls the JS method. "No calls captured" means
   "the theme did not scroll deliberately", NOT "nothing scrolled".

   ── v2 fixes, all three found by Quinn testing v1 before Steve spent a round ──

   1. BLOCKER: v1 recorded any delta > 30px, so ordinary scrolling filled the
      buffer and every false entry was stamped "HEIGHT STEADY (something
      scrolled)" — one of the two answers this tool exists to choose between.
      Steve would have screenshotted a box that had already decided. Worse, the
      scroll-back after a real jump evicted the real jump from the ring buffer.
   2. `js calls:` was a global last-6-ever list, never windowed, so calls from
      seconds earlier got stapled onto an event.
   3. The box could grow over the swatch row and eat taps.

   ── v3 fixes, from Quinn verifying v2 by manufacturing the real event ──

   4. paint() ran only when a jump fired, so taps that caused nothing left the
      counters stale — the box would read `taps 0` after three taps and look
      like a dead instrument, in exactly the case worth screenshotting. Now
      repaints on touchend and on a 250ms tick.
   5. The patch self-test was console-only, and a phone has no console. A
      silently failed patch would make every event read `calls=NONE`, which
      would be read as a finding rather than a broken tool. Now `patch OK` /
      `patch FAILED` sits in the header, and the self-test's own call is
      discarded so it cannot attach to a real event.

   ── Telling a tap from a drag: order, not elapsed time (v3) ──

   v2 required "no touchmove in the last 400ms", because momentum scrolling on
   iOS continues after touchend firing no touch events at all, so a plain
   touchmove check lets a flick through. But that window also rejected a
   legitimate tap made shortly after a scroll — precisely what happens when you
   scroll back down and tap the next colour — and would have cost a round.

   v3 tests ORDER instead: a jump counts when the last gesture was a genuine
   TAP (touchstart -> touchend, no touchmove, under 600ms), the jump lands
   within 2s of it, no touchmove has occurred SINCE that tap ended, and the
   single-frame delta exceeds 100px. Momentum always follows a drag, so its
   touchmove is newer than any earlier tap and is rejected; movement that
   merely preceded the tap is irrelevant and no longer penalised. No "pause
   before tapping" instruction needed.

   ── Empty results have to be readable ──

   A filter this aggressive can silently eat the very event we are hunting, and
   then "nothing recorded" is indistinguishable from "nothing happened". So the
   status line always shows taps / drags / jumps seen / recorded / filtered.
   If it says jumps=3 recorded=0 the filter is wrong, and that is a finding.

   Recording FREEZES after 4 events so the first real jump can never be pushed
   out by later scrolling.

   ── v8: OFF BY DEFAULT AGAIN. ?mpdebug=scroll is the only switch ──

   v6 armed on any theme whose role is not 'main', which put a black readout
   across the bottom of every BUILD preview page — over the corner widgets and
   in the way of reviewing anything else on the draft (Steve, 2026-09-16).
   Back to the explicit URL param, which is what v5 should have used: Shopify
   strips ITS OWN preview_theme_id on the redirect that sets the preview
   cookie, but preserves other params, so mpdebug survives the hop.

       on : https://monkeyandpebbles.com/products/<handle>?mpdebug=scroll
       off: any URL without it

   The param persists only for the page it is on — navigating away disarms it,
   which for a hunt that lives on one PDP is the right trade for not having a
   diagnostic sitting on top of every other review.
*/
;(() => {
  const armed = /[?&]mpdebug=scroll/.test(window.location.search)
  if (!armed) return

  const MAX_EVENTS = 4
  const events = []
  const calls = []
  const stats = {taps: 0, drags: 0, jumps: 0, filtered: 0}
  let patchOk = null // null = untested, true/false after the self-test

  // Touch gesture state
  const TAP_SLOP_PX = 12
  let touchStartT = 0
  let touchStartX = 0
  let touchStartY = 0
  let touchMoved = false
  let lastTouchMoveT = -1e9
  let lastWheelT = -1e9
  let lastTapEndT = -1e9
  let lastTapTarget = ''

  const now = () => performance.now()

  const describe = (el) => {
    if (!el) return 'null'
    const tag = el.tagName ? el.tagName.toLowerCase() : '?'
    const cls =
      el.className && typeof el.className === 'string'
        ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
        : ''
    return tag + cls + (el.value ? '[' + el.value + ']' : '')
  }

  // ---- 1. Deliberate-scroll routes, each timestamped so events can window them
  const record = (name) => {
    calls.push({name: name, t: now()})
    if (calls.length > 40) calls.shift()
  }
  const wrap = (obj, name, label) => {
    const orig = obj[name]
    if (typeof orig !== 'function') return
    obj[name] = function () {
      record(label || name)
      return orig.apply(this, arguments)
    }
  }
  wrap(window, 'scrollTo')
  wrap(window, 'scroll')
  wrap(window, 'scrollBy')
  wrap(Element.prototype, 'scrollIntoView')
  wrap(HTMLElement.prototype, 'focus')
  try {
    const d = Object.getOwnPropertyDescriptor(Element.prototype, 'scrollTop')
    if (d && d.set) {
      Object.defineProperty(Element.prototype, 'scrollTop', {
        configurable: true,
        get: d.get,
        set: function (v) {
          if (this === document.scrollingElement || this === document.body) {
            record('scrollTop=')
          }
          return d.set.call(this, v)
        },
      })
    }
  } catch (e) {
    record('scrollTop PATCH FAILED')
  }

  // ---- 2. Separate a tap from a drag ------------------------------------
  document.addEventListener(
    'touchstart',
    (e) => {
      touchStartT = now()
      touchMoved = false
      const t0 = e.touches && e.touches[0]
      touchStartX = t0 ? t0.clientX : 0
      touchStartY = t0 ? t0.clientY : 0
      lastTapTarget = describe(e.target)
    },
    {passive: true, capture: true},
  )
  document.addEventListener(
    'touchmove',
    (e) => {
      // v4: only DISTANCE reclassifies a tap as a drag — not the mere
      // existence of a touchmove.
      //
      // Quinn probed this in Chromium with a trusted 3px-jitter tap and it
      // survived, but only because Blink's touch-slop threshold swallows the
      // event before the listener ever sees it. That is engine behaviour, not
      // this code, and WebKit's slop is a different number we cannot check —
      // and WebKit is the ONLY place this tool will ever run. If a wobbling
      // finger on a 22px swatch got classed as a drag, `lastTapEndT` would
      // never update, the jump would be silently filtered, and the one
      // instrument we have would come back empty.
      //
      // Quinn rated the residual risk low and did not ask for this. I am
      // adding it anyway: it is six lines, a real drag travels far more than
      // 12px, and it removes an unverifiable engine dependency from the exact
      // spot where we have no way to verify.
      //
      // `lastTouchMoveT` still updates on EVERY touchmove — the ordering test
      // needs it, and a within-tap wobble is harmless there because it lands
      // before `lastTapEndT`.
      lastTouchMoveT = now()
      const t0 = e.touches && e.touches[0]
      if (!t0) {
        touchMoved = true
        return
      }
      const dx = t0.clientX - touchStartX
      const dy = t0.clientY - touchStartY
      if (Math.sqrt(dx * dx + dy * dy) > TAP_SLOP_PX) touchMoved = true
    },
    {passive: true, capture: true},
  )
  document.addEventListener(
    'touchend',
    () => {
      const dur = now() - touchStartT
      if (!touchMoved && dur < 600) {
        stats.taps++
        lastTapEndT = now()
      } else {
        stats.drags++
      }
      // Repaint here too. paint() previously ran only when a jump fired, so a
      // tap that caused nothing left the box reading `taps 0` after three
      // taps — which reads as "the instrument never saw me", the one wrong
      // conclusion to invite in the case we most want screenshotted.
      paint()
    },
    {passive: true, capture: true},
  )

  // v7: DESKTOP. A mouse produces no touch events at all, so `lastTapEndT`
  // never moved, `sinceTap` stayed astronomical, and EVERY jump on desktop was
  // filtered by construction — while the box told the reader "the filter is
  // wrong", pointing them at the tool instead of the bug. Steve's most recent
  // recording is desktop, so this was the case most likely to be run next.
  // Found by Quinn clicking with a real mouse rather than re-reading the code.
  //
  // Keyed on `pointerup` + pointerType 'mouse', NOT on `click`: a touch also
  // emits a synthetic click ~300ms later, which would double-count every phone
  // tap and quietly push `lastTapEndT` later, widening the 2s window.
  document.addEventListener(
    'pointerup',
    (e) => {
      if (e.pointerType !== 'mouse') return
      stats.taps++
      lastTapEndT = now()
      lastTapTarget = describe(e.target)
      paint()
    },
    {passive: true, capture: true},
  )

  // The hole Quinn's fix would otherwise open: on desktop the scroll gesture is
  // the WHEEL, which fires no pointer events. Without this, a click followed by
  // a trackpad flick inside the 2s window would log momentum as tap-caused —
  // the exact false positive the touch ordering test exists to prevent. Same
  // test, same reasoning, different input device.
  document.addEventListener(
    'wheel',
    () => {
      lastWheelT = now()
    },
    {passive: true, capture: true},
  )

  // ---- 3. Sample layout every frame -------------------------------------
  let prevY = window.scrollY
  let prevH = document.documentElement.scrollHeight
  let frozen = false

  const sample = () => {
    const y = window.scrollY
    const h = document.documentElement.scrollHeight
    const delta = y - prevY

    if (Math.abs(delta) > 30) {
      stats.jumps++
      const t = now()
      const sinceTap = t - lastTapEndT
      // Abrupt, shortly after a real tap, with NO finger movement since that
      // tap ended.
      //
      // v3: this replaces a `time since last touchmove > 400ms` window. That
      // window filtered a legitimate tap made shortly after scrolling — which
      // is exactly what happens when you scroll back down and tap the next
      // colour — and it would have cost a round. Ordering is the right test,
      // not elapsed time: momentum scrolling always FOLLOWS a drag, so its
      // touchmove is newer than any earlier tap and gets rejected, while a tap
      // made half a second after a scroll is still clean because the movement
      // preceded it.
      const noMoveSinceTap =
        lastTouchMoveT < lastTapEndT && lastWheelT < lastTapEndT
      const qualifies =
        Math.abs(delta) > 100 && noMoveSinceTap && sinceTap < 2000

      if (qualifies && !frozen) {
        events.push({
          from: Math.round(prevY),
          to: Math.round(y),
          hFrom: prevH,
          hTo: h,
          focus: describe(document.activeElement),
          sinceTap: Math.round(sinceTap),
          tapped: lastTapTarget,
          // Only calls in the 500ms before this event — a global list would
          // staple load-time scrolling onto the jump.
          calls:
            calls
              .filter((c) => t - c.t < 500)
              .map((c) => c.name)
              .join(' | ') || 'NONE',
        })
        if (events.length >= MAX_EVENTS) frozen = true
        paint()
      } else if (!qualifies) {
        stats.filtered++
        paint()
      }
    }
    prevY = y
    prevH = h
    requestAnimationFrame(sample)
  }

  // ---- 4. Render ---------------------------------------------------------
  let box
  const paint = () => {
    if (!box) return
    // Patch health belongs in the header, not the console: on a phone there is
    // no console, and a silently-failed patch would make every event read
    // `calls=NONE` — which would be taken as a finding rather than a broken
    // instrument.
    const head =
      `MP scroll debug v8 [patch ${patchOk === null ? '?' : patchOk ? 'OK' : 'FAILED'}] — ` +
      `taps ${stats.taps} · drags ${stats.drags} · ` +
      `jumps ${stats.jumps} · logged ${events.length} · filtered ${stats.filtered}`
    if (!events.length) {
      box.textContent =
        head +
        '\n' +
        (stats.jumps
          ? 'Jumps seen but none matched a tap. If this stays 0 logged after a\n' +
            'swatch tap, the filter is wrong — screenshot it anyway, that is data.'
          : 'Armed. Scroll to the swatches and tap a colour.')
      return
    }
    box.textContent =
      head +
      (frozen ? '  [FROZEN — screenshot this]' : '') +
      '\n' +
      events
        .map(
          (e, i) =>
            `#${i + 1} y ${e.from}->${e.to}  h ${e.hFrom}->${e.hTo}\n` +
            `   ${e.hTo < e.hFrom ? '*** PAGE GOT SHORTER (clamp) ***' : '*** HEIGHT STEADY (actively scrolled) ***'}\n` +
            `   ${e.sinceTap}ms after tap on ${e.tapped}\n` +
            `   focus=${e.focus}  calls=${e.calls}`,
        )
        .join('\n')
  }

  const start = () => {
    box = document.createElement('pre')
    box.setAttribute('data-mp-scroll-debug', '')
    box.style.cssText = [
      'position:fixed',
      'left:0',
      'right:0',
      'bottom:0',
      'z-index:2147483647',
      'margin:0',
      'padding:6px 8px',
      'max-height:40vh',
      'overflow:hidden',
      // Never intercept a tap — it is read-only output. Without this the box
      // grows over the swatch row and eats the very taps we are measuring.
      'pointer-events:none',
      'background:rgba(0,0,0,0.88)',
      'color:#0f0',
      'font:10px/1.3 ui-monospace,Menlo,monospace',
      'white-space:pre-wrap',
    ].join(';')
    document.body.appendChild(box)
    paint()
    requestAnimationFrame(sample)
    // Self-test: prove the patch captures before trusting an empty result.
    const before = calls.length
    window.scrollTo(0, window.scrollY)
    patchOk = calls.length - before === 1
    calls.length = 0 // drop the self-test call so it cannot attach to an event
    paint()
    // Belt and braces: a slow tick keeps the counters honest even if no touch
    // event and no jump ever fires.
    setInterval(paint, 250)
    console.log('[MP] scroll debug v8 armed · patch', patchOk ? 'OK' : 'FAILED')
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start)
  } else {
    start()
  }
})()